import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Cell,
} from "recharts";
import type { PipelineFunnelData } from "@/hooks/dashboard/useDashboardKPIs";
import { KPITooltip } from "./KPITooltip";

const STAGE_COLORS: Record<string, string> = {
	matched: "#6366f1",
	applied: "#3b82f6",
	screening: "#0ea5e9",
	interview: "#14b8a6",
	offer: "#f59e0b",
	hired: "#22c55e",
	closed: "#ef4444",
	sent: "#8b5cf6",
};

interface FunnelChartProps {
	data: PipelineFunnelData[];
	tooltip?: string;
}

export function FunnelChart({ data, tooltip }: FunnelChartProps) {
	const { t } = useTranslation();

	const chartData = data.map(d => ({
		...d,
		label: t(`dashboard.kpi.stages.${d.status}`, d.status),
	}));

	return (
		<Card>
			<CardHeader className={data.every(d => d.count === 0) ? "pb-2" : undefined}>
				<div className="flex items-center gap-2">
					<CardTitle className="text-lg">{t("dashboard.kpi.pipelineFunnel")}</CardTitle>
					{tooltip && <KPITooltip text={tooltip} />}
				</div>
			</CardHeader>
			<CardContent>
				{data.every(d => d.count === 0) ? (
					<div className="text-muted-foreground text-sm py-2">
						{t("dashboard.kpi.noData")}
					</div>
				) : (
					<div className="h-[300px]">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart
								data={chartData}
								layout="vertical"
								margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
							>
								<CartesianGrid strokeDasharray="3 3" opacity={0.1} />
								<XAxis type="number" />
								<YAxis type="category" dataKey="label" width={75} tick={{ fontSize: 12 }} />
								<Tooltip
									contentStyle={{
										backgroundColor: "rgba(255, 255, 255, 0.95)",
										borderRadius: "0.5rem",
										border: "none",
										boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
									}}
								/>
								<Bar dataKey="count" radius={[0, 4, 4, 0]}>
									{chartData.map((entry, index) => (
										<Cell key={index} fill={STAGE_COLORS[data[index].status] || "#94a3b8"} />
									))}
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
